<template>
  <div class="container-fluid mt-5">
    <div>
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row">
            <div class="col-10">
              <h3 class="mb-0 view-header-buttons">
                {{ cafeteria.name }}
                <base-button
                  v-if="$currentUserCan($permissions.PERM_EDIT_CAFETERIAS)"
                  type="primary"
                  icon
                  size="sm"
                  @click="editCafeteria()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="$currentUserCan($permissions.PERM_DELETE_CAFETERIAS)"
                  type="danger"
                  icon
                  size="sm"
                  @click="deleteCafeteria()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </base-button>
              </h3>
            </div>
            <div class="col-2 text-right">
              <base-button @click="goBack()" type="button" class="btn btn-sm btn-primary">
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>
            </div>
          </div>
        </template>
        <div>
          <div class="col-12 justify-content-center justify-content-sm-between flex-wrap">
            <tabs
              fill
              class="flex-column flex-md-row"
              tabNavWrapperClasses="nav-wrapper"
              tabNavClasses="nav nav-pills nav-fill"
              value="global"
            >
              <card shadow>
                <tab-pane title="global" id="1" :active="true">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GLOBAL") }}
                  </span>
                  <cafeteria-view-global :cafeteria="cafeteria" @cafeteriaUpdated="get" />
                </tab-pane>

                <tab-pane
                  v-if="cafeteria.has_warehouse"
                  title="establishment-products"
                  id="2"
                >
                  <span slot="title">
                    <i class="fa fa-cubes" />
                    {{ $t("COMMON.PRODUCTS") }}
                  </span>
                  <cafeteria-view-warehouse-products :cafeteria="cafeteria" />
                </tab-pane>

                <tab-pane
                  v-if="cafeteria.operation_type === OPERATION_TYPE_PRODUCTION"
                  title="producible-products"
                  id="4"
                >
                  <span slot="title">
                    <i class="fa fa-industry" />
                    {{ $t("CAFETERIAS.PRODUCIBLE_PRODUCTS") }}
                  </span>
                  <cafeteria-view-producible-products
                    :cafeteria="cafeteria"
                    @cafeteriaUpdated="get"
                  />
                </tab-pane>

                <tab-pane
                  title="allowed-users"
                  id="3"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_USERS)"
                >
                  <span slot="title">
                    <i class="ni ni-single-02" />
                    {{ $t("COMMON.USERS") }}
                  </span>
                  <allowed-users-view
                    :object="cafeteria"
                    :objectName="cafeteria.name"
                    :objectStore="'cafeterias'"
                    @objectUpdated="get"
                  />
                </tab-pane>
                <tab-pane title="equipments" id="998">
                  <span slot="title">
                    <i class="ni ni-folder-17" />
                    {{ $t("COMMON.EQUIPMENTS") }}
                  </span>
                  <cafeteria-view-equipments :cafeteria="cafeteria" />
                </tab-pane>
                <tab-pane
                  title="logs"
                  id="999"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOGS)"
                >
                  <span slot="title">
                    <i class="fa fa-file" />
                    {{ $t("COMMON.LOGS") }}
                  </span>
                  <cafeteria-view-logs :cafeteria="cafeteria" />
                </tab-pane>
              </card>
            </tabs>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep } from "lodash";
import swal from "sweetalert2";
import { Tabs, TabPane } from "@/components";
import AllowedUsersView from "@/components/AllowedUsers/AllowedUsersView.vue";
import { OPERATION_TYPE_PRODUCTION } from "@/constants/cafeteria";
import defaultCafeteria from "./defaultCafeteria";
import CafeteriaViewGlobal from "./partials/CafeteriaViewGlobal.vue";
import CafeteriaViewLogs from "./partials/CafeteriaViewLogs.vue";
import CafeteriaViewEquipments from "./partials/CafeteriaViewEquipments.vue";
import CafeteriaViewWarehouseProducts from "./partials/CafeteriaViewWarehouseProducts.vue";
import CafeteriaViewProducibleProducts from "./partials/CafeteriaViewProducibleProducts.vue";

export default {
  layout: "DashboardLayout",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    Tabs,
    TabPane,
    CafeteriaViewGlobal,
    CafeteriaViewLogs,
    AllowedUsersView,
    CafeteriaViewWarehouseProducts,
    CafeteriaViewEquipments,
    CafeteriaViewProducibleProducts,
  },

  mixins: [],

  data() {
    return {
      cafeteria: cloneDeep(defaultCafeteria),
      OPERATION_TYPE_PRODUCTION,
    };
  },

  computed: {},

  watch: {
    "$route.params.id": function () {
      this.get();
    },
  },

  created() {
    this.get();
  },

  methods: {
    async get() {
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("cafeterias/get", id);
        this.cafeteria = this.$store.getters["cafeterias/cafeteria"];
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async editCafeteria(row) {
      this.$router.push({
        name: "Edit Cafeteria",
        params: { id: this.cafeteria.id },
      });
    },

    async deleteCafeteria() {
      const confirmation = await swal.fire({
        title: this.$t("CAFETERIAS.DELETE_THIS_CAFETERIA"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("cafeterias/destroy", this.cafeteria.id);
          await this.goBack();
          this.$notify({
            type: "success",
            message: this.$t("CAFETERIAS.CAFETERIA_DELETED"),
          });
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    goBack() {
      this.$router.push({ name: "List Cafeterias" });
    },
  },
};
</script>
